/**
 * A página de cada seção — para o sumário e a lista de figuras.
 *
 * O texto do documento **corre**: quem pagina é o navegador, e não há como saber,
 * só de olhar o documento, em que folha o "Capítulo 3" caiu (um parágrafo a mais
 * no capítulo 1 empurra tudo). Por isso o número da página **não sai de conta
 * feita no documento** — sai de uma medição: o navegador desenha o papel, informa a
 * posição de cada título na folha contínua, e a conta de "em que página isto cai"
 * vem para cá, para o motor, onde pode ser testada sem navegador.
 *
 * É o mesmo caminho do Word: ele pagina duas vezes — a primeira para medir, a
 * segunda para escrever o número no sumário. Aqui o "medir" é do navegador (o site
 * levanta as posições) e o "escrever" é do motor.
 *
 * A conta é feita sobre o **papel contínuo** que o desenho monta: tudo empilhado,
 * sem quebra, e a posição (`topoMm`) de cada seção medida a partir do alto do
 * papel. Dividir essa posição pela altura da mancha de uma folha dá quantas folhas
 * já passaram — e é aí que entra o detalhe de cada documento.
 */
import { espacoDeUmaLinhaMm, geometriaDoPapel } from './abnt.js';
import { formaDeABRIR } from './abertura.js';
import { papelDoDocumento } from './tipos.js';
/**
 * A página de cada seção — a conta que o sumário e a lista de figuras usam.
 *
 * O algoritmo é o de um livro, com três detalhes honestos:
 *
 * 1. **a seção primária abre folha nova** (`break-before: page`): se ela cairia no
 *    meio da folha, o navegador a empurra para a seguinte — e a conta faz o mesmo;
 * 2. **a folha inteira** (a folha de anotação) não é de texto: ela ocupa a própria
 *    página e o texto depois dela recomeça na seguinte;
 * 3. **`primeiraPaginaDoTexto`**: quando a pessoa numera só a partir do texto (a
 *    norma faz a contagem correr desde a capa, mas o número só aparecer no texto),
 *    o primeiro capítulo é a página 1 — o deslocamento é aplicado no fim.
 */
export function paginasDoSumario(documento, medidas, opcoes = {}) {
    const geometria = opcoes.geometria ?? geometriaDoPapel(papelDoDocumento(documento));
    const alturaDaMancha = geometria.alturaManchaMm;
    const passo = formaDeABRIR(documento);
    const porId = new Map(medidas.map((medida) => [medida.id, medida.topoMm]));
    const paginas = new Map();
    /*
     * `folhasExtras` é o quanto a paginação já andou "para além" das folhas de
     * mancha: cada folha inteira aberta soma uma página cheia que o papel contínuo
     * não registra na posição do título seguinte (o texto recomeça embaixo dela).
     */
    let folhasExtras = 0;
    let topoAnterior = 0;
    for (const item of passo) {
        const topo = porId.get(item.id);
        if (topo === undefined) {
            /*
             * Seção sem medida (não apareceu no DOM): fica na mesma folha da anterior.
             * O editor lista todas, mas um filtro pode esconder uma; melhor um número
             * aproximado do que nenhum.
             */
            paginas.set(item.id, Math.max(1, (paginas.get(passo[0]?.id ?? '') ?? 1) + folhasExtras));
            continue;
        }
        /*
         * Quantas folhas de mancha o topo do título já passou. O `+ 1e-6` cuida da
         * borda: um título no milímetro exato `n * altura` está na folha `n + 1`.
         */
        const manchas = Math.max(0, Math.floor((topo + 1e-6) / alturaDaMancha));
        let pagina = manchas + 1 + folhasExtras;
        /*
         * A abertura de folha: se o título não está logo no começo de uma folha, o
         * `break-before` o leva para a próxima. A tolerância é a de duas linhas do
         * corpo — um título que cai a um milímetro da borda já está "no começo".
         */
        if (item.abreFolhaNova && topo > topoAnterior) {
            const noComeco = Math.abs(topo - manchas * alturaDaMancha) < espacoDeUmaLinhaMm(geometria) * 2;
            if (!noComeco)
                pagina += 1;
        }
        paginas.set(item.id, pagina);
        topoAnterior = topo;
        /*
         * As folhas inteiras desta seção empurram a seguinte: cada uma é uma página
         * que não conta como mancha de texto. A conta mantém o texto depois da
         * anotação **depois** dela, e não sobreposto.
         */
        folhasExtras += item.folhasInteirasAntes;
    }
    /*
     * `primeiraPaginaDoTexto`: as primeiras peças são pré-textuais (capa, rosto,
     * resumo, sumário) e entram na contagem; o sumário da norma, porém, começa a
     * numeração no primeiro capítulo. Deslocamos para que o primeiro item do texto
     * vire a página pedida.
     */
    const primeiraDoTexto = opcoes.primeiraPaginaDoTexto;
    if (primeiraDoTexto && primeiraDoTexto > 1) {
        const primeiroId = passo[0]?.id;
        const base = primeiroId ? (paginas.get(primeiroId) ?? primeiraDoTexto) : primeiraDoTexto;
        const deslocamento = base - primeiraDoTexto;
        if (deslocamento !== 0) {
            for (const [id, pagina] of paginas)
                paginas.set(id, pagina - deslocamento);
        }
    }
    return paginas;
}
/**
 * A peça está **antes** do texto (é pré-textual)?
 *
 * Exportado para o desenho saber quando a numeração "do texto" começa.
 */
export function ehPreTextual(elemento) {
    return [
        'CAPA',
        'FOLHA_DE_ROSTO',
        'FOLHA_DE_APROVACAO',
        'DEDICATORIA',
        'AGRADECIMENTOS',
        'EPIGRAFE',
        'RESUMO',
        'ABSTRACT',
        'RESUMO_DO_ARTIGO',
        'APRESENTACAO',
        'LISTA_DE_FIGURAS',
        'LISTA_DE_TABELAS',
        'LISTA_DE_ABREVIATURAS',
        'SUMARIO',
    ].includes(elemento);
}
//# sourceMappingURL=paginacao.js.map